import React, { useState, useEffect } from "react";
import HeaderFilter from "../general/HeaderFilter";
import { MainContainer } from "./styles";
import GridView from "../general/GridView";
import {
  filterProducts,
  getProviderProducts,
  getRetailerProducts,
} from "./utils";
import {
  readExcel,
  transformExcelToArticles,
} from "../general/HeaderFilter/utils";
import useNotifications from "../../hooks/useNotification";
import UploadTemplateModal from "./UploadTemplateModal";
import axios from "axios";
import CustomNotification from "../general/CustomNotification";
import { Snackbar, Alert } from "@mui/material";

const Tasks = ({ user }) => {
  const [gridView, setGridView] = useState(true);
  const [gridData, setGridData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [headersTable, setHeadersTable] = useState([]);
  const [headerMapping, setHeaderMapping] = useState([]);
  const [percentageParams, setPercentageParams] = useState([]);
  const [providersDataFilter, setProvidersDataFilter] = useState([]);
  const [phasesDataFilter, setPhasesDataFilter] = useState([]);
  const [groupsDataFilter, setGroupsDataFilter] = useState([]);
  const [productInfo, setProductInfo] = useState([]);
  const [productCount, setProductCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filterLists, setFilterLists] = useState(null);
  const [selectedArticles, setSelectedArticles] = useState([]);
  const [dataDetailTable, setDataDetailTable] = useState([]);
  const isProvider = user.is_retailer === 0 && user.id_role === 0;
  const [selectedProvider, setSelectedProvider] = useState();
  const [selectedPhase, setSelectedPhase] = useState();
  const [selectedGroup, setSelectedGroup] = useState();
  const [searchValue, setSearchValue] = useState("");
  const [showModalUpload, setShowModalUpload] = useState(false);
  const [excelData, setExcelData] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [laodingTemplate, setLoadingTemplate] = useState(false);
  const [uploadingTemplate, setUploadingTemplate] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const { filteredGridData, filteredTableData } = filterProducts(
    gridData,
    tableData,
    searchValue
  );

  const { notifications, addNotification, deleteNotification } =
    useNotifications();

  useEffect(() => {
    // Variable para controlar si el efecto sigue siendo válido
    let isCancelled = false;

    const fetchProductsRecursive = async () => {
      setLoading(true);

      // IMPORTANTE: Limpiar los estados antes de empezar para que la UI no tenga basura
      setGridData([]);
      setTableData([]);

      let allGridData = [];
      let allTableData = [];
      let allDataDetail = [];
      let allPercentageParams = [];

      let currentStartIndex = 0;
      let keepFetching = true;
      const PAGE_SIZE = 20;
      let filtersLoaded = false;

      while (keepFetching) {
        // Si el filtro cambió mientras estábamos en el bucle, abortamos este bucle
        if (isCancelled) return;

        const params = {
          canonicFilter: null,
          productsType: "onboarding",
          providerFilter: selectedProvider || "",
          phaseFilter: selectedPhase || "",
          groupFilter: selectedGroup || "",
          startIndex: currentStartIndex,
          setFilterLists: filtersLoaded ? () => {} : setFilterLists,
        };

        const response = isProvider
          ? await getProviderProducts(params, setProductCount)
          : await getRetailerProducts(params, setProductCount);

        if (isCancelled) return; // Chequeo doble después del await

        if (response.err || !response.gridData || response.productList.length === 0) {
          keepFetching = false;
          break;
        }

        allGridData = [...allGridData, ...response.gridData];
        allTableData = [...allTableData, ...response.tableData];
        allDataDetail = [...allDataDetail, ...response.dataDetailTable];
        allPercentageParams = [...allPercentageParams, ...response.percentageParams];

        if (!filtersLoaded) {
          setHeadersTable(response.headersTable);
          setHeaderMapping(response.headerMapping);
          setProvidersDataFilter(response.providersDataFilter);
          setPhasesDataFilter(response.phasesDataFilter);
          setGroupsDataFilter(response.groupsDataFilter);
          setProductInfo(response.dataRadio);
          filtersLoaded = true;
        }

        currentStartIndex += PAGE_SIZE;
      }

      // Solo actualizamos el estado si el componente no se ha vuelto a renderizar con otros filtros
      if (!isCancelled) {
        setGridData(allGridData);
        setTableData(allTableData);
        setDataDetailTable(allDataDetail);
        setPercentageParams(allPercentageParams);
        setLoading(false);
      }
    };

    fetchProductsRecursive();

    // Función de limpieza: se ejecuta cuando el filtro cambia o el componente se desmonta
    return () => {
      isCancelled = true;
    };
  }, [selectedProvider, selectedPhase, selectedGroup, searchValue]);

  useEffect(() => {
    setSelectedArticles([]);
  }, [gridView]);

  const handleSearchChange = (value) => {
    setSearchValue(value);
  };

  const handleOnDownloadTemplate = async () => {
    // imprimir los articulos seleccionados
    const articles = gridView ? filteredGridData : filteredTableData;

    const selectedItems = articles.filter((item) =>
      selectedArticles.some(
        (selected) => selected.id_article === item.id_article
      )
    );

    if (selectedItems.length === 0) {
      addNotification("error", "Por favor, selecciona al menos un artículo");
      return;
    }

    const data = selectedItems.map((item) => ({
      articleId: item.id_article,
      versionId: item.version,
      categoryId: item.id_category,
      retailers: item.retailers.map((retailer) => {
        return { id: retailer };
      }),
    }));

    try {
      setLoadingTemplate(true);
      const res = await axios.post(
        process.env.REACT_APP_INVOKE_PRODUCTS_TEMPLATE,
        { articles: data },
        {
          headers: {
            Authorization: sessionStorage.getItem("jwt"),
          },
        }
      );

      if (res.data.statusCode === 200) {
        setSnackbar({
          open: true,
          message:
            "La plantilla se ha creado correctamente y el correo está en camino.",
          severity: "success",
        });
      } else {
        setSnackbar({
          open: true,
          message: "Error al procesar la plantilla",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error al descargar la plantilla:", error);
      setSnackbar({
        open: true,
        message: "Error al generar la plantilla",
        severity: "error",
      });
    } finally {
      setLoadingTemplate(false);
    }
  };

  const handleOnUploadTemplate = () => {
    // Crear un input temporal para abrir el explorador de archivos
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".xlsx, .xls";
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (file) {
        const fileExtension = file.name.split(".").pop().toLowerCase();
        if (fileExtension === "xlsx" || fileExtension === "xls") {
          setSelectedFile(file);
          addNotification("success", `Archivo seleccionado: ${file.name}`);

          try {
            const data = await readExcel(file);
            const articles = transformExcelToArticles(data);

            console.log("Artículos generados:", articles);

            setExcelData({ rawData: data, articles });
            addNotification("success", "Archivo procesado correctamente");
            // Abrir el modal solo después de procesar el archivo
            setShowModalUpload(true);
          } catch (error) {
            addNotification("error", "Error al leer el archivo");
            console.error(error);
          }
        } else {
          addNotification(
            "error",
            "Por favor, selecciona un archivo Excel (.xlsx o .xls)"
          );
        }
      }
    };
    input.click();
  };

  const validateAndSetFile = async (file) => {
    const fileExtension = file.name.split(".").pop().toLowerCase();
    if (fileExtension === "xlsx" || fileExtension === "xls") {
      setSelectedFile(file);
      addNotification("success", `Archivo seleccionado: ${file.name}`);

      // Procesar el archivo automáticamente
      try {
        const data = await readExcel(file);
        const articles = transformExcelToArticles(data);

        console.log("Artículos generados:", articles);

        setExcelData({ rawData: data, articles });
        addNotification("success", "Archivo procesado correctamente");
      } catch (error) {
        addNotification("error", "Error al leer el archivo");
        console.error(error);
      }

      return true;
    } else {
      addNotification(
        "error",
        "Por favor, selecciona un archivo Excel (.xlsx o .xls)"
      );
      return false;
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const file = event.dataTransfer.files[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  const handleChangeFile = () => {
    setSelectedFile(null);
    setExcelData(null);
  };

  const handleCloseModal = () => {
    setShowModalUpload(false);
    setSelectedFile(null);
    setExcelData(null);
  };

  const handleSaveExcel = async () => {
    try {
      setUploadingTemplate(true);
      const response = await axios.post(
        process.env.REACT_APP_UPLOAD_PRODUCTS_TEMPLATE,
        { articles: excelData.articles },
        {
          headers: {
            Authorization: sessionStorage.getItem("jwt"),
          },
        }
      );
      console.log(response);
      addNotification("success", "Datos guardados correctamente");
    } catch (error) {
      console.error("Error al subir los datos:", error);
      addNotification("error", "Error al subir los datos");
      return;
    } finally {
      setUploadingTemplate(false);
      handleCloseModal();
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <MainContainer>
      <UploadTemplateModal
        open={showModalUpload}
        onClose={handleCloseModal}
        excelData={excelData}
        selectedFile={selectedFile}
        onFileChange={handleFileChange}
        onChangeFile={handleChangeFile}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onSave={handleSaveExcel}
        uploading={uploadingTemplate}
      />
      <HeaderFilter
        searchBar
        placeholderSearchBar="Buscar producto"
        filterProvider
        filterPhase
        filterGroup
        showMultipleEdition={!isProvider}
        showView
        showReport
        showExportable={false} //!isProvider
        showDownloadTemplate={true}
        onDownloadTemplate={handleOnDownloadTemplate}
        loadingTemplate={laodingTemplate}
        showUploadTemplate={true}
        onUploadTemplate={handleOnUploadTemplate}
        gridView={gridView}
        setGridView={setGridView}
        providersFilter={providersDataFilter}
        setProvidersFilter={(value) => setSelectedProvider(value)}
        phasesFilter={phasesDataFilter}
        setPhasesFilter={(value) => setSelectedPhase(value)}
        groupsFilter={groupsDataFilter}
        setGroupsFilter={(value) => setSelectedGroup(value)}
        onSearchChange={handleSearchChange}
        isProvider={isProvider}
        selectedArticles={selectedArticles}
        setSelectedArticles={setSelectedArticles}
      />

      <GridView
        isGridView={gridView}
        data={{ tableData: filteredTableData, gridData: filteredGridData }}
        headers={headersTable}
        headerMapping={headerMapping}
        percentageParams={percentageParams}
        typePercentage="percentageRequired"
        cardsPerPage={12}
        rowsPerPage={15}
        selectedArticles={selectedArticles}
        setSelectedArticles={setSelectedArticles}
        dataDetailTable={dataDetailTable}
        productInfo={productInfo}
        showEditButtonTable={isProvider}
        textIsNotData="Al parecer no hay productos para tu fase"
        isGeneralLoading={loading}
      />

      <CustomNotification
        open={notifications.length > 0}
        alerts={notifications}
        deleteNotification={deleteNotification}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MainContainer>
  );
};

export default Tasks;
